import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { Tenant } from '@etiquetachef/types';

type Props = {
  tenant: Tenant;
  /** Altura do iframe do mapa (default `20rem`). */
  height?: string;
};

/**
 * Embed do Google Maps centrado no estabelecimento + botão "Abrir no Maps".
 * Reutilizado pela aba Local (`TenantLocationTab`) e pelo modal "Ver no
 * Local" do `TenantInfoCard` — `placeId` é o canônico (estável); cai para
 * coordenadas; sem nenhum dos dois, mostra fallback "sem mapa".
 *
 * Dicas: a chave é `VITE_GOOGLE_MAPS_API_KEY_WEB` (pública, restrita por
 * referrer no console do Google); sem ela não há iframe, só o link externo.
 */
export function TenantMapEmbed({ tenant, height = '20rem' }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const address = tenant.address ?? '';
  const placeId = tenant.placeId ?? null;
  const latitude = tenant.latitude ?? null;
  const longitude = tenant.longitude ?? null;

  const mapsKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY_WEB ?? '';
  let embedSrc: string | null = null;
  if (mapsKey.length > 0) {
    if (placeId !== null && placeId.length > 0) {
      const params = new URLSearchParams({ key: mapsKey, q: `place_id:${placeId}` });
      embedSrc = `https://www.google.com/maps/embed/v1/place?${params.toString()}`;
    } else if (latitude !== null && longitude !== null) {
      const params = new URLSearchParams({
        key: mapsKey,
        q: `${latitude.toString()},${longitude.toString()}`,
      });
      embedSrc = `https://www.google.com/maps/embed/v1/place?${params.toString()}`;
    }
  }

  let externalHref: string | null = null;
  if (placeId !== null && placeId.length > 0) {
    externalHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address.length > 0 ? address : 'place',
    )}&query_place_id=${placeId}`;
  } else if (latitude !== null && longitude !== null) {
    externalHref = `https://www.google.com/maps/search/?api=1&query=${latitude.toString()},${longitude.toString()}`;
  }

  return (
    <SpaceBetween size="m">
      {externalHref ? (
        <Button href={externalHref} target="_blank" iconAlign="right" iconName="external">
          {t('tenantDetail.location.openInMaps')}
        </Button>
      ) : null}

      {embedSrc ? (
        <iframe
          title={t('tenantDetail.location.mapTitle')}
          src={embedSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          style={{ height, width: '100%', border: 0, borderRadius: 8 }}
          allow="geolocation"
        />
      ) : (
        <Box fontSize="body-s" color="text-status-inactive">
          {t('tenantDetail.location.noMap')}
        </Box>
      )}
    </SpaceBetween>
  );
}
